import axios from 'axios';
import api from '@/Configs/ApiConfig';

const API_URL_ENDPOINT = '/operations/signals/thesis';

const state = {
    // Thesis settings
    settings: {
        selectedContinents: [],
        selectedCountries: [],
        includedSectors: [],
        excludedSectors: [],
        business_model: '',
        allowStealth: false,
        selectSector: false,
    },

    // Available options (populated from props)
    availableData: {
        countries: [],
        continents: [],
        sectors: [],
        businessModels: [],
    },

    // Saving state
    isSaving: false,
    saveStatus: null, // Can be 'saving', 'success', or 'error'
    saveErrorMessage: '',

    // Tracking state
    isInitialized: false,
};

const getters = {
    /**
     * Get the current settings
     */
    settings: state => state.settings,

    /**
     * Get saving states
     */
    isSaving: state => state.isSaving,
    saveStatus: state => state.saveStatus,
    saveStatusText: state => {
        if (state.saveStatus === 'saving') return 'Saving changes...';
        if (state.saveStatus === 'success') return 'Changes saved';
        if (state.saveStatus === 'error') return state.saveErrorMessage || 'Failed to save';
        return '';
    },

    /**
     * Get available data
     */
    countries: state => state.availableData.countries,
    continents: state => state.availableData.continents,
    sectors: state => state.availableData.sectors,
    businessModels: state => state.availableData.businessModels,
};

const mutations = {
    /**
     * Initialize the store with available data
     */
    SET_AVAILABLE_DATA(state, { countries, continents, sectors, businessModels }) {
        state.availableData = {
            countries: countries || [],
            continents: continents || [],
            sectors: sectors || [],
            businessModels: businessModels || [],
        };
    },

    /**
     * Initialize settings from thesis data
     */
    INITIALIZE_SETTINGS(state, thesisData) {
        const settings = {
            selectedContinents: [],
            selectedCountries: [],
            includedSectors: [],
            excludedSectors: [],
            business_model: '',
            allowStealth: thesisData.stealth || false,
            selectSector: thesisData.sector_agnostic || false,
        };

        // Initialize continents
        if (thesisData.continents && thesisData.continents.length > 0) {
            for (const continentId of thesisData.continents) {
                const continentObj = state.availableData.continents.find(
                    continent => continent.id === continentId
                );
                if (continentObj) {
                    settings.selectedContinents.push(continentObj);
                }
            }
        }

        // Initialize countries
        if (thesisData.countries && thesisData.countries.length > 0) {
            for (const countryId of thesisData.countries) {
                const countryObj = state.availableData.countries.find(
                    country => country.id === countryId
                );
                if (countryObj) {
                    settings.selectedCountries.push(countryObj);
                }
            }
        }

        // Initialize included sectors
        if (thesisData.sectors && thesisData.sectors.length > 0) {
            for (const sectorId of thesisData.sectors) {
                const sectorObj = state.availableData.sectors.find(
                    sector => sector.id === sectorId
                );
                if (sectorObj) {
                    settings.includedSectors.push(sectorObj);
                }
            }
        }

        // Initialize excluded sectors
        if (thesisData.exclude_sectors && thesisData.exclude_sectors.length > 0) {
            for (const sectorId of thesisData.exclude_sectors) {
                const sectorObj = state.availableData.sectors.find(
                    sector => sector.id === sectorId
                );
                if (sectorObj) {
                    settings.excludedSectors.push(sectorObj);
                }
            }
        }

        // Initialize business model
        if (thesisData.business_models && thesisData.business_models.length > 0) {
            let businessModelsCount = 0;
            for (const businessModelId of thesisData.business_models) {
                const businessModel = state.availableData.businessModels.find(
                    model => model.id === businessModelId
                );
                if (businessModel) {
                    settings.business_model = businessModel.name.toLowerCase();
                    businessModelsCount++;
                }
            }
            if (businessModelsCount > 1) {
                settings.business_model = 'both';
            }
        }

        state.settings = settings;
        state.isInitialized = true;
    },

    /**
     * Update selected continents
     */
    SET_SELECTED_CONTINENTS(state, continents) {
        state.settings.selectedContinents = continents;
    },

    /**
     * Update selected countries
     */
    SET_SELECTED_COUNTRIES(state, countries) {
        state.settings.selectedCountries = countries;
    },

    /**
     * Update included sectors
     */
    SET_INCLUDED_SECTORS(state, sectors) {
        state.settings.includedSectors = sectors;
    },

    /**
     * Update excluded sectors
     */
    SET_EXCLUDED_SECTORS(state, sectors) {
        state.settings.excludedSectors = sectors;
    },

    /**
     * Update business model
     */
    SET_BUSINESS_MODEL(state, model) {
        state.settings.business_model = model;
    },

    /**
     * Update stealth setting
     */
    SET_ALLOW_STEALTH(state, allow) {
        state.settings.allowStealth = allow;
    },

    /**
     * Update sector agnostic setting
     */
    SET_SELECT_SECTOR(state, isAgnostic) {
        state.settings.selectSector = isAgnostic;
    },

    /**
     * Update saving state
     */
    SET_IS_SAVING(state, isSaving) {
        state.isSaving = isSaving;
    },

    /**
     * Update save status
     */
    SET_SAVE_STATUS(state, status) {
        state.saveStatus = status;
    },

    /**
     * Update save error message
     */
    SET_SAVE_ERROR_MESSAGE(state, message) {
        state.saveErrorMessage = message;
    }
};

const actions = {
    /**
     * Initialize the store with data
     */
    initializeStore({ commit }, { thesis, countries, continents, sectors, businessModels }) {
        // Set available data
        commit('SET_AVAILABLE_DATA', { countries, continents, sectors, businessModels });

        // Initialize settings from thesis data
        commit('INITIALIZE_SETTINGS', thesis);
    },

    /**
     * Update selected continents
     */
    updateSelectedContinents({ commit }, continents) {
        commit('SET_SELECTED_CONTINENTS', continents);
    },

    /**
     * Update selected countries
     */
    updateSelectedCountries({ commit }, countries) {
        commit('SET_SELECTED_COUNTRIES', countries);
    },

    /**
     * Update included sectors
     */
    updateIncludedSectors({ commit }, sectors) {
        commit('SET_INCLUDED_SECTORS', sectors);
    },

    /**
     * Update excluded sectors
     */
    updateExcludedSectors({ commit }, sectors) {
        commit('SET_EXCLUDED_SECTORS', sectors);
    },

    /**
     * Set the business model
     */
    setBusinessModel({ commit }, model) {
        commit('SET_BUSINESS_MODEL', model);
    },

    /**
     * Set the stealth setting
     */
    setAllowStealth({ commit }, allow) {
        commit('SET_ALLOW_STEALTH', allow);
    },

    /**
     * Set the sector agnostic setting
     */
    setSelectSector({ commit }, isAgnostic) {
        commit('SET_SELECT_SECTOR', isAgnostic);
    },

    /**
     * Clear geography selections
     */
    clearGeography({ commit }) {
        commit('SET_SELECTED_COUNTRIES', []);
    },

    /**
     * Toggle continent selection
     */
    toggleContinent({ commit, state, getters }, continent) {
        const isSelected = state.settings.selectedContinents.some(c => c.id === continent.id) ||
                          state.settings.selectedCountries.some(c => c.id === continent.id);

        if (isSelected) {
            const filteredCountries = state.settings.selectedCountries.filter(
                item => item.id !== continent.id
            );
            commit('SET_SELECTED_COUNTRIES', filteredCountries);
        } else {
            commit('SET_SELECTED_COUNTRIES', [...state.settings.selectedCountries, continent]);
        }
    },

    /**
     * Save thesis settings to API
     */
    async saveThesis({ commit, state, dispatch }) {
        // Don't attempt to save if already in progress
        if (state.isSaving) return;

        try {
            commit('SET_IS_SAVING', true);
            commit('SET_SAVE_STATUS', 'saving');

            // Show a saving toast
            dispatch('toast/info', {
                title: 'Saving changes...',
                description: 'Your thesis preferences are being updated'
            }, { root: true });

            // Convert settings to the format expected by the API
            const business_models = [];
            if (state.settings.business_model === 'both') {
                state.availableData.businessModels.forEach((model) =>
                    business_models.push(model.id)
                );
            } else if (state.settings.business_model === 'b2b') {
                const b2b = state.availableData.businessModels.find(
                    (m) => m.name.toLowerCase() === 'b2b'
                );
                if (b2b) business_models.push(b2b.id);
            } else if (state.settings.business_model === 'b2c') {
                const b2c = state.availableData.businessModels.find(
                    (m) => m.name.toLowerCase() === 'b2c'
                );
                if (b2c) business_models.push(b2c.id);
            }

            const payload = {
                countries: state.settings.selectedCountries.map(
                    (location) => location.id
                ),
                continents: state.settings.selectedContinents.map(
                    (continent) => continent.id
                ),
                sectors: state.settings.includedSectors.map(
                    (sector) => sector.id
                ),
                exclude_sectors: state.settings.excludedSectors.map(
                    (sector) => sector.id
                ),
                business_models: business_models,
                stealth: state.settings.allowStealth,
                sector_agnostic: state.settings.selectSector,
            };

            const response = await api.post(API_URL_ENDPOINT, payload);

            if (response.data.success || response.status === 302 || response.status === 200) {
                commit('SET_SAVE_STATUS', 'success');
                commit('SET_SAVE_ERROR_MESSAGE', '');

                // Set a timeout to clear the save status after 3 seconds
                setTimeout(() => {
                    if (state.saveStatus === 'success') {
                        commit('SET_SAVE_STATUS', null);
                    }
                }, 3000);

                // Show success toast
                dispatch('toast/success', {
                    title: 'Thesis saved',
                    description: response.data.message || 'Your thesis preferences have been updated'
                }, { root: true });
            }
        } catch (error) {
            commit('SET_SAVE_STATUS', 'error');
            commit('SET_SAVE_ERROR_MESSAGE', error.response?.data?.message || 'Failed to save');

            dispatch('toast/error', {
                title: 'Failed to save thesis',
                description: error.response?.data?.message || 'Please try again'
            }, { root: true });

            console.error('Error saving thesis:', error);

            // Clear error status after 5 seconds
            setTimeout(() => {
                if (state.saveStatus === 'error') {
                    commit('SET_SAVE_STATUS', null);
                }
            }, 5000);
        } finally {
            commit('SET_IS_SAVING', false);
        }
    }
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};
